import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  IRolesRepository,
  Role,
  CreateRoleInput,
  UpdateRoleInput,
  Permission,
  PermissionAction,
} from '@erp/data-access/interfaces';

interface RoleRow {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  description: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

interface PermissionRow {
  role_id: string;
  module: string;
  action: PermissionAction;
}

interface MemberRoleRow {
  role_id: string;
}

/** Wraps PostgrestError into a real Error so consumer code's
 *  `e instanceof Error` checks match. Same helper as in
 *  invitations-repository.ts — kept private here to avoid a shared file. */
function rpcError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const e = err as { message?: string; code?: string; details?: string; hint?: string };
    const wrapped = new Error(e.message ?? 'rpc_failed');
    if (e.code) (wrapped as Error & { code?: string }).code = e.code;
    if (e.details) (wrapped as Error & { details?: string }).details = e.details;
    if (e.hint) (wrapped as Error & { hint?: string }).hint = e.hint;
    return wrapped;
  }
  return new Error(String(err));
}

/** Roles + role_permissions live on master alongside tenants/tenant_members.
 *  Mutations route through SECURITY DEFINER RPCs (create/update/delete_custom_role).
 *  Reads are RLS-scoped to current_user_tenant_id(). */
export class SupabaseRolesRepository implements IRolesRepository {
  constructor(private supabase: SupabaseClient) {}

  async list(): Promise<Role[]> {
    // Roles for this tenant — RLS filters automatically.
    const { data: roles, error } = await this.supabase
      .from('roles')
      .select('*')
      .order('is_system', { ascending: false })
      .order('slug', { ascending: true });
    if (error) throw rpcError(error);
    const roleRows = (roles ?? []) as RoleRow[];
    if (roleRows.length === 0) return [];

    const ids = roleRows.map((r) => r.id);

    // One round trip for permissions, one for member rows. Two parallel
    // requests keep latency low. Member count is computed in JS — tenant
    // memberships are tiny (typically < 20 per tenant).
    const [permsRes, membersRes] = await Promise.all([
      this.supabase.from('role_permissions').select('*').in('role_id', ids),
      this.supabase.from('tenant_members').select('role_id').in('role_id', ids),
    ]);
    if (permsRes.error) throw rpcError(permsRes.error);
    if (membersRes.error) throw rpcError(membersRes.error);

    const byRole = new Map<string, Permission[]>();
    for (const p of (permsRes.data ?? []) as PermissionRow[]) {
      const list = byRole.get(p.role_id);
      if (list) list.push({ module: p.module, action: p.action });
      else byRole.set(p.role_id, [{ module: p.module, action: p.action }]);
    }

    const countByRole = new Map<string, number>();
    for (const m of (membersRes.data ?? []) as MemberRoleRow[]) {
      countByRole.set(m.role_id, (countByRole.get(m.role_id) ?? 0) + 1);
    }

    return roleRows.map((r) =>
      mapRole(r, byRole.get(r.id) ?? [], countByRole.get(r.id) ?? 0),
    );
  }

  async getById(id: string): Promise<Role | null> {
    const { data, error } = await this.supabase
      .from('roles')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw rpcError(error);
    if (!data) return null;
    const { data: perms, error: perr } = await this.supabase
      .from('role_permissions')
      .select('*')
      .eq('role_id', id);
    if (perr) throw rpcError(perr);
    const list = ((perms ?? []) as PermissionRow[]).map((p) => ({
      module: p.module,
      action: p.action,
    }));
    return mapRole(data as RoleRow, list);
  }

  async create(input: CreateRoleInput): Promise<Role> {
    const { data, error } = await this.supabase.rpc('create_custom_role', {
      p_name: input.name,
      p_description: input.description ?? null,
      p_permissions: input.permissions ?? [],
    });
    if (error) throw rpcError(error);
    const created = data as RoleRow;
    const fetched = await this.getById(created.id);
    if (!fetched) throw new Error('role_not_found_after_create');
    return fetched;
  }

  async update(id: string, input: UpdateRoleInput): Promise<Role> {
    const { data, error } = await this.supabase.rpc('update_custom_role', {
      p_role_id: id,
      p_name: input.name ?? null,
      p_description: input.description ?? null,
      p_permissions: input.permissions ?? null,
    });
    if (error) throw rpcError(error);
    const updated = data as RoleRow;
    const fetched = await this.getById(updated.id);
    if (!fetched) throw new Error('role_not_found_after_update');
    return fetched;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.rpc('delete_custom_role', {
      p_role_id: id,
    });
    if (error) throw rpcError(error);
  }
}

function mapRole(
  r: RoleRow,
  permissions: Permission[],
  memberCount?: number,
): Role {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    slug: r.slug,
    name: r.name,
    description: r.description,
    isSystem: r.is_system,
    permissions,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    ...(memberCount !== undefined ? { memberCount } : {}),
  };
}
